<script setup lang="ts">
useHead({
  title: "Zenapptic AI - Creative Services",
});

const appMounted = useState("appMounted");
const { sendScreenView } = useAnalytics();
if (appMounted.value) sendAnalytics();
watch(appMounted, () => sendAnalytics);

function sendAnalytics() {
  sendScreenView({ type: "PageView", target: "CreativeServices" }, true);
}

const currentRoute = useRoute();

let hashObserver: IntersectionObserver | null = null;
let pauseObserver = false;

onMounted((): void => {
  hashObserver = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      if (!pauseObserver) {
        if (entries[0].isIntersecting) {
          location.hash = `#${entries[0].target.id}`;
        }
      }
    },
    {
      threshold: 0.7,
    }
  );
  document.querySelectorAll(".observable").forEach((el) => hashObserver!.observe(el));
  onNavBarClick();
});

function onNavBarClick(): void {
  pauseObserver = true;
  setTimeout(() => {
    pauseObserver = false;
  }, 2000);
}
</script>

<template>
  <div class="creative-services">
    <div class="cs-nav">
      <div class="brand">
        <div>
          <NuxtLink href="/#feature">
            <img :src="`${useCdnUrl()}/assets/logos/zen-2023-footer.png`" />
          </NuxtLink>
        </div>
      </div>

      <nav class="sub-nav">
        <ul>
          <li>
            <a
              href="#demoReel"
              :class="{ active: currentRoute.hash === '#demoReel' }"
              @click="onNavBarClick()"
              >Services</a
            >
          </li>
          <li>
            <a
              href="#about"
              :class="{ active: currentRoute.hash === '#about' }"
              @click="onNavBarClick()"
              >About</a
            >
          </li>
          <li>
            <a
              href="#team"
              :class="{ active: currentRoute.hash === '#team' }"
              @click="onNavBarClick()"
              >CS Team</a
            >
          </li>
        </ul>
      </nav>
    </div>

    <div id="observerRoot">
      <section id="demoReel" class="observable">
        <creative-features />
      </section>

      <section id="about" class="container observable">
        <div class="cs-about">
          <div>
            <h2>Award-Winning Graphics</h2>

            <p>
              The Zenapptic creative group is made up of seasoned industry professionals from the
              broadcast world who have delivered some of the highest profile productions in
              television, including Super Bowl XXXIII, the World Series, US Presidential elections
              as well as projects for ABC, NBC, CNN, CNBC, ESPN, FOX, Global, CBC/Radio Canada and
              many others. Our team has also won a number of prestigious design awards from the
              Broadcast Design Association (BDA) and OMNI Intermedia for work on design projects in
              the U.S., Canada and Europe.
            </p>

            <p>
              We believe our strength lies in our deep understanding of all types of graphics, be it
              real-time, pre-rendered or VR/AR, and in our ability to communicate data eloquently.
              The channel through which the information is delivered varies from TV screens, to
              mobile devices, VR goggles or video walls in public squares. However, the ever-present
              constant is our skill to interpret the information in such way that it tells a story
              and clearly communicate data on a human level.
            </p>
          </div>
        </div>
      </section>

      <section id="team" class="container observable">
        <div class="cs-team">
          <div>
            <h2>CS Team</h2>
            <div class="team-profiles">
              <div class="profile">
                <div class="profile-image">
                  <img
                    :src="`${useCdnUrl()}/assets/creative-services/anton-profile.webp`"
                    alt="Anton Maxinovsky"
                  />
                </div>
                <h3><span>Anton</span><br /><span>Maximovsky</span></h3>
                <p>
                  Head of the Creative Services Team, 25+ years of experience in broadcast design
                  and winner of multiple BDA awards.
                </p>
              </div>
              <div class="profile">
                <div class="profile-image">
                  <img
                    :src="`${useCdnUrl()}/assets/creative-services/sam-profile.webp`"
                    alt="Sam Hatem"
                  />
                </div>
                <h3><span>Sam</span><br /><span>Hatem</span></h3>
                <p>
                  Head of Production, Real-time 3D and AR specialist with 25+ years of experience in
                  broadcast design.
                </p>
              </div>
              <div class="profile">
                <div class="profile-image">
                  <img
                    :src="`${useCdnUrl()}/assets/creative-services/mark-profile.webp`"
                    alt="Mark Phaneuf"
                  />
                </div>
                <h3><span>Mark</span><br /><span>Phaneuf</span></h3>
                <p>
                  Award winning Creative Director and Lead Designer with almost two decades of
                  broadcast design experience.
                </p>
              </div>
              <div class="profile">
                <div class="profile-image">
                  <img
                    :src="`${useCdnUrl()}/assets/creative-services/lev-profile.webp`"
                    alt="Lev Antonovich"
                  />
                </div>
                <h3><span>Lev</span><br /><span>Antonovich</span></h3>
                <p>
                  Creative Services Team Coordinator with almost a decade of experience managing
                  international projects.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss">
@use "../assets/scss/vars";
.creative-services {
  color: white;
  .cs-nav {
    position: fixed;
    z-index: 60;

    @media only screen and (max-width: 600px) {
      display: block;
      width: 100%;
    }

    .brand {
      align-items: center;
      background: #282728 url(/assets/creative-services/dotted-background.png);

      display: grid;
      height: 220px;
      justify-items: center;
      width: 270px;

      @media only screen and (max-width: 600px) {
        height: 40px;
        justify-items: flex-start;
        padding: 0 1rem;
        width: 100%;

        .zen-logo {
          height: 40px;
          width: 40px;
        }
      }

      div {
        display: inline-block;
        a {
          align-items: center;
          display: inline-grid;
          grid-auto-flow: column;
          text-decoration: none;

          h1 {
            color: #fff;
            font-family: "pragmatica", sans-serif;
            font-size: 2rem;
            font-weight: 700;
            margin: 0;
            padding: 0;
            text-decoration: none;
          }

          img {
            height: 100px;
            margin-right: 2rem;

            @media only screen and (max-width: 768px) {
              height: 30px;
            }
          }

          span {
            color: vars.$primary-color;
          }
        }
      }
    }

    nav {
      padding-top: 2rem;

      @media only screen and (max-width: 600px) {
        padding-top: 0;
      }

      ul {
        display: grid;
        justify-content: end;
        list-style-type: none;
        margin: 0;
        padding: 0;

        @media only screen and (max-width: 600px) {
          grid-template-columns: 1fr 1fr 1fr;
          justify-content: center;
        }

        li {
          margin: 3px 0;
          text-align: right;

          @media only screen and (max-width: 600px) {
            margin: 0;
            text-align: center;
          }

          a {
            background: black;
            border-bottom: black solid 3px;
            color: white;
            display: inline-block;
            font-size: 1.7rem;
            padding: 0.5rem 2rem;
            padding-bottom: calc(0.5rem - 3px);
            text-decoration: none;
            text-transform: uppercase;
            transition: all 0.2s ease-out;

            @media only screen and (max-width: 600px) {
              padding: 0.5rem 0;
              width: 100%;
            }

            &:hover {
              border-color: white;
            }

            &.active {
              background: white;
              color: black;
            }
          }
        }
      }
    }
  }

  .cs-about {
    align-items: center;
    display: grid;
    grid-template-columns: 270px 1fr;
    margin: auto;
    padding-top: 8rem;
    width: 100%;

    @media only screen and (max-width: 600px) {
      grid-template-columns: 1fr;
      height: auto;
      padding-top: 10rem;
    }

    > div {
      grid-column-start: 2;
      padding: 2rem 2rem 2rem 4rem;

      @media only screen and (max-width: 600px) {
        grid-column-start: 1;
      }

      h2 {
        font-size: 4rem;
        font-weight: 500;
        text-transform: uppercase;
      }

      p {
        font-size: 1.8rem;
      }
    }
  }

  .cs-team {
    align-items: center;
    display: grid;
    grid-template-columns: 270px 1fr;
    height: 100vh;
    margin: auto;
    width: 100%;

    @media only screen and (max-width: 600px) {
      grid-template-columns: 1fr;
      height: auto;
      padding-top: 10rem;
    }

    > div {
      grid-column-start: 2;
      padding: 2rem 2rem 2rem 4rem;

      @media only screen and (max-width: 600px) {
        grid-column-start: 1;
      }

      h2 {
        font-size: 4rem;
        font-weight: 500;
        text-transform: uppercase;
      }

      .team-profiles {
        display: grid;
        grid-gap: 3rem;
        grid-template-columns: repeat(4, auto);

        /* Portrait */
        @media only screen and (min-width: 600px) and (max-width: 1365px) {
          grid-template-columns: repeat(2, auto);
        }

        @media only screen and (max-width: 600px) {
          grid-auto-flow: row;
          grid-template-columns: 1fr;
          justify-items: center;
        }

        .profile {
          width: 232px;

          h3 {
            color: white;
            font-size: 2rem;
            font-weight: 500;
            line-height: 1;
            text-transform: uppercase;

            span {
              background: #4c4c4c;
              padding: 0 0.75rem;
            }
          }

          p {
            font-size: 1.4rem;
          }

          .profile-image {
            width: 232px;

            img {
              width: 100%;
            }
          }

          @media only screen and (min-width: 600px) and (max-width: 1024px) {
            width: 150px;

            .profile-image {
              width: 150px;
            }
          }
        }
      }
    }
  }
}
</style>
